import { DNDFieldsEnum } from "./Types";
import { spaTooltipHeaders } from "./TooltipInformation";

/** Payload field keys for Spa → Retail Sub Categories (one level per row). */
export const SPA_RETAIL_SUB_CATEGORY_FIELD_NUMBER = "Sub Category Number";
export const SPA_RETAIL_SUB_CATEGORY_FIELD_NAME = "Sub Category Name";
/** @deprecated Legacy payload key; read when loading saved data. */
export const SPA_RETAIL_SUB_CATEGORY_FIELD_VALUE_LEGACY = "Sub Category Value";
export const SPA_RETAIL_SUB_CATEGORY_FIELD_ACTIVE = "Active";

export const SPA_RETAIL_SUB_CATEGORY_LEVELS = [1, 2, 3, 4, 5] as const;

export type SpaRetailSubCategoryLevel =
  (typeof SPA_RETAIL_SUB_CATEGORY_LEVELS)[number];

export const getSpaRetailSubCategoryLevelLabel = (
  level: SpaRetailSubCategoryLevel,
): string => `Level ${level}`;

export const SPA_RETAIL_SUB_CATEGORY_LEGACY_KEYS = [
  DNDFieldsEnum.SUBCATEGORY1,
  DNDFieldsEnum.SUBCATEGORY2,
  DNDFieldsEnum.SUBCATEGORY3,
  DNDFieldsEnum.SUBCATEGORY4,
  DNDFieldsEnum.SUBCATEGORY5,
] as const;

export const SPA_RETAIL_SUB_CATEGORY_SAMPLE_MARKER = "sample_test_agilysys";

export const SPA_RETAIL_SUB_CATEGORY_PAGE_TITLE = "Retail Sub Categories";
export const SPA_RETAIL_SUB_CATEGORY_LABEL_NUMBER = "Sub Category Number *";
export const SPA_RETAIL_SUB_CATEGORY_LABEL_NAME = "Sub Category Name *";
export const SPA_RETAIL_SUB_CATEGORY_LABEL_ACTIVE = "Active";
export const SPA_RETAIL_SUB_CATEGORY_SELECT_LEVEL_PLACEHOLDER = "Select level";
export const SPA_RETAIL_SUB_CATEGORY_LIST_TITLE =
  "Retail Sub Categories Available";
export const SPA_RETAIL_SUB_CATEGORY_EMPTY_LIST_MESSAGE =
  "No Retail Sub Category Information Available";
export const SPA_RETAIL_SUB_CATEGORY_BUTTON_SUFFIX = "Retail Sub Category";
export const SPA_RETAIL_SUB_CATEGORY_SAMPLE_WARNING =
  "Do not use sample data. Those are for demo purposes only!";

export const SPA_RETAIL_SUB_CATEGORY_TOOLTIPS = {
  header: spaTooltipHeaders.retailSubCategoryHeader,
  number: spaTooltipHeaders.retailSubCategorySubcategory1,
  name: spaTooltipHeaders.retailSubCategorySubcategory1,
  active: spaTooltipHeaders.retailSubCategoryActive,
} as const;

export const SPA_RETAIL_SUB_CATEGORY_VALIDATION_MESSAGES = {
  numberMandatory: "Sub Category Number is mandatory",
  nameMandatory: "Sub Category Name is mandatory",
  duplicateEntry: "Cannot have duplicate entries",
} as const;

export type SpaRetailSubCategoryPayload = {
  [SPA_RETAIL_SUB_CATEGORY_FIELD_NUMBER]: SpaRetailSubCategoryLevel | "";
  [SPA_RETAIL_SUB_CATEGORY_FIELD_NAME]: string | null;
  [SPA_RETAIL_SUB_CATEGORY_FIELD_ACTIVE]: boolean;
};

export const rowContainsSpaRetailSubCategorySample = (obj: any): boolean =>
  Object.values(obj ?? {}).some((value: any) => {
    if (typeof value === "string") {
      return value
        .toLowerCase()
        .includes(SPA_RETAIL_SUB_CATEGORY_SAMPLE_MARKER);
    }
    if (Array.isArray(value)) {
      return value.some(
        (item) =>
          typeof item === "string" &&
          item.toLowerCase().includes(SPA_RETAIL_SUB_CATEGORY_SAMPLE_MARKER),
      );
    }
    return false;
  });

export const isSpaRetailSubCategorySampleValue = (value: unknown): boolean =>
  typeof value === "string" &&
  value.toLowerCase().includes(SPA_RETAIL_SUB_CATEGORY_SAMPLE_MARKER);

export const isSpaRetailSubCategoryActive = (
  record: Record<string, unknown>,
): boolean => {
  const active = record[SPA_RETAIL_SUB_CATEGORY_FIELD_ACTIVE];
  return active !== false && active !== "No" && active !== "no";
};

export const parseSpaRetailSubCategoryLevelFromRow = (
  list: Record<string, unknown> | null | undefined,
): SpaRetailSubCategoryLevel | "" => {
  const level = Number(list?.[SPA_RETAIL_SUB_CATEGORY_FIELD_NUMBER]);
  return SPA_RETAIL_SUB_CATEGORY_LEVELS.includes(
    level as SpaRetailSubCategoryLevel,
  )
    ? (level as SpaRetailSubCategoryLevel)
    : "";
};

export const isSpaRetailSubCategoryRowActive = (
  list: Record<string, unknown> | null | undefined,
): boolean => list?.[SPA_RETAIL_SUB_CATEGORY_FIELD_ACTIVE] !== false;

export const getSpaRetailSubCategoryNameFromRow = (
  record: Record<string, unknown> | null | undefined,
): string | null => {
  const name = record?.[SPA_RETAIL_SUB_CATEGORY_FIELD_NAME];
  if (name != null && String(name).trim()) {
    return String(name);
  }
  const legacy = record?.[SPA_RETAIL_SUB_CATEGORY_FIELD_VALUE_LEGACY];
  return legacy != null ? String(legacy) : null;
};

export const buildSpaRetailSubCategoryPayload = (
  subCategoryNumber: SpaRetailSubCategoryLevel | "",
  subCategoryName: string | null,
  active: boolean,
): SpaRetailSubCategoryPayload => ({
  [SPA_RETAIL_SUB_CATEGORY_FIELD_NUMBER]: subCategoryNumber,
  [SPA_RETAIL_SUB_CATEGORY_FIELD_NAME]: subCategoryName,
  [SPA_RETAIL_SUB_CATEGORY_FIELD_ACTIVE]: active,
});

export const isSpaRetailSubCategoryDuplicateEntry = (
  items: any[],
  number: SpaRetailSubCategoryLevel | "",
  value: string | null,
  excludeId?: string | null,
): boolean => {
  if (number === "" || !value?.trim()) {
    return false;
  }
  return items.some(
    (item: any) =>
      String(item.id) !== String(excludeId ?? "") &&
      Number(item[SPA_RETAIL_SUB_CATEGORY_FIELD_NUMBER]) === Number(number) &&
      String(getSpaRetailSubCategoryNameFromRow(item) ?? "").trim() ===
        value.trim(),
  );
};

/** Display-only DND field key; not saved to payload. */
export const SPA_RETAIL_SUB_CATEGORY_DND_LEVEL_LABEL = "Sub Category Level";

export const formatSpaRetailSubCategoriesForDnd = (items: any[]): any[] =>
  items.map((item) => {
    const level = parseSpaRetailSubCategoryLevelFromRow(item);
    const name = getSpaRetailSubCategoryNameFromRow(item);
    return {
      ...item,
      [SPA_RETAIL_SUB_CATEGORY_FIELD_NAME]: name,
      [SPA_RETAIL_SUB_CATEGORY_DND_LEVEL_LABEL]:
        level !== ""
          ? getSpaRetailSubCategoryLevelLabel(level)
          : String(item[SPA_RETAIL_SUB_CATEGORY_FIELD_NUMBER] ?? ""),
    };
  });

export const stripSpaRetailSubCategoryDndDisplayFields = (
  items: any[],
): any[] =>
  items.map(
    ({ [SPA_RETAIL_SUB_CATEGORY_DND_LEVEL_LABEL]: _, ...rest }) => rest,
  );

const isFlatSpaRetailSubCategoryRow = (
  record: Record<string, unknown>,
): boolean => SPA_RETAIL_SUB_CATEGORY_FIELD_NUMBER in record;

/** Build level 1–5 option lists from flat or legacy retail sub category rows. */
export const buildSpaRetailSubCategoryOptionsByLevel = (
  rows: unknown[] | null | undefined,
): string[][] => {
  const buckets: string[][] = [[], [], [], [], []];
  const list = Array.isArray(rows) ? rows : [];

  for (const row of list) {
    if (!row || typeof row !== "object") {
      continue;
    }

    const record = row as Record<string, unknown>;
    if (!isSpaRetailSubCategoryActive(record)) {
      continue;
    }

    if (isFlatSpaRetailSubCategoryRow(record)) {
      const flatNum = record[SPA_RETAIL_SUB_CATEGORY_FIELD_NUMBER];
      const flatVal = getSpaRetailSubCategoryNameFromRow(record);
      if (
        flatNum != null &&
        flatVal != null &&
        String(flatVal).trim() &&
        !isSpaRetailSubCategorySampleValue(flatVal)
      ) {
        const idx = Number(flatNum) - 1;
        if (idx >= 0 && idx < 5) {
          buckets[idx].push(String(flatVal));
        }
      }
      continue;
    }

    SPA_RETAIL_SUB_CATEGORY_LEGACY_KEYS.forEach((key, index) => {
      const value = record[key];
      if (
        value != null &&
        String(value).trim() &&
        !isSpaRetailSubCategorySampleValue(value)
      ) {
        buckets[index].push(String(value));
      }
    });
  }

  return buckets;
};
